// A line-for-line port of the formulas in Kayla's "Momeaze Budget Model" artifact (Oct 27, 2026).
// Don't change the math here without changing the source model too: scripts/import-budget.ts
// reruns the original page's code against this port and flags any difference.

export const MAXM = 60;
export const GROUPS = [
  { key: "people", name: "People" },
  { key: "acq", name: "Acquisition: ads & influencers" },
  { key: "serving", name: "AI & hosting" },
  { key: "software", name: "Software & tools" },
  { key: "events", name: "Events & travel" },
  { key: "ops", name: "Operations" },
  { key: "pro", name: "Professional services" },
  { key: "other", name: "Other" },
] as const;
const LINE_GROUPS = ["people", "software", "events", "ops", "pro", "other"];

export type Model = { drivers: Record<string, any>; owners: any[]; costs: any[]; events?: any[]; ads: any[] };
export type Facts = { policyMonths?: number; debt?: { balance?: number; writtenPlan?: { month: string; amount: number }[] } & Record<string, any> };
export type Ctx = { runsPaid: number | null; paid: Set<string> };

const num = (v: any, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const pad2 = (n: number) => String(n).padStart(2, "0");
export const keyOf = (d: Date) => d.getFullYear() + "-" + pad2(d.getMonth() + 1);
export const addM = (k: string, n: number) => {
  let [y, m] = k.split("-").map(Number) as [number, number];
  m += n;
  y += Math.floor((m - 1) / 12);
  m = (((m - 1) % 12) + 12) % 12 + 1;
  return y + "-" + pad2(m);
};
const mBetween = (a: string, b: string) => {
  const [ya, ma] = a.split("-").map(Number) as [number, number];
  const [yb, mb] = b.split("-").map(Number) as [number, number];
  return (yb - ya) * 12 + (mb - ma);
};
const dim = (k: string) => {
  const [y, m] = k.split("-").map(Number) as [number, number];
  return new Date(y, m, 0).getDate();
};
const mStart = (k: string) => {
  const [y, m] = k.split("-").map(Number) as [number, number];
  return new Date(y, m - 1, 1);
};
const lc = (s: any) => String(s || "").trim().toLowerCase();
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const isMonth = (k: any) => /^\d{4}-\d{2}$/.test(k || "");

export function adSpend(m: Model, p: any, k: string) {
  const o = p.overrides || {};
  if (o[k] !== undefined && o[k] !== null && o[k] !== "") return Math.max(0, num(o[k]));
  return isMonth(m.drivers.launch) && mBetween(m.drivers.launch, k) >= 0 ? Math.max(0, num(p.monthly)) : 0;
}

// Users and booked revenue, month by month from launch.
export function usersSim(m: Model, last: string) {
  const d = m.drivers;
  const out: Record<string, any> = {};
  const L = d.launch;
  if (!isMonth(L) || L > last) return out;
  const a = clamp(num(d.annualMix) / 100, 0, 1), churn = clamp(num(d.churn) / 100, 0, 1), rho = clamp(num(d.annualRenew) / 100, 0, 1);
  const fee = clamp(num(d.storeFee) / 100, 0, 1), pm = num(d.priceMonthly), pa = num(d.priceAnnual);
  const ramp = Math.max(0, num(d.ramp, 1));
  const i2p = Math.max(0.001, num(d.installToPaid) / 100);
  const pFrom = isMonth(d.partnersFrom) ? d.partnersFrom : L;
  const CH = ["org", "ads", "inf", "part"] as const;
  const Mo: Record<string, number> = { org: 0, ads: 0, inf: 0, part: 0 };
  const coh: Record<string, { n: number; count: number }[]> = { org: [], ads: [], inf: [], part: [] };
  for (let k = L, n = 0; k <= last && n <= MAXM + 36; k = addM(k, 1), n++) {
    const r = Math.pow(ramp, n);
    const launchConv = n === 0 ? Math.max(0, num(d.paidStart)) : 0;
    const organic = Math.max(0, num(d.organicNew)) * r;
    const adsBy: Record<string, number> = {};
    let adsUsers = 0;
    for (const p of m.ads || []) {
      const sp = adSpend(m, p, k);
      const u = num(p.cac) > 0 ? sp / num(p.cac) : 0;
      adsBy[p.id] = u;
      adsUsers += u;
    }
    const creators = Math.max(0, n === 0 ? num(d.infLaunchCreators) : num(d.infCreators));
    const infFee = creators * Math.max(0, n === 0 ? num(d.infLaunchFee) : num(d.infFee));
    const infUsers = creators * Math.max(0, num(d.infUsersPerCreator)) * r;
    const partners =
      k >= pFrom ? Math.max(0, num(d.partnersStart)) + Math.max(0, num(d.partnersNew)) * (mBetween(pFrom, k) + 1) : Math.max(0, num(d.partnersStart));
    const partUsers = partners * Math.max(0, num(d.partnerUsers)) * r;
    const nw: Record<string, number> = { org: launchConv + organic, ads: adsUsers, inf: infUsers, part: partUsers };
    let grossM = 0, grossA = 0, cancels = 0, annualActive = 0, paying = 0;
    const grossBy: Record<string, number> = {};
    for (const c of CH) {
      const x = nw[c]!;
      const lost = Mo[c]! * churn;
      cancels += lost;
      Mo[c] = Mo[c]! - lost + x * (1 - a);
      let renewed = 0;
      for (const h of coh[c]!) {
        const age = n - h.n;
        if (age > 0 && age % 12 === 0) {
          const kept = h.count * rho;
          cancels += h.count - kept;
          h.count = kept;
          renewed += kept;
        }
      }
      if (x * a > 0) coh[c]!.push({ n, count: x * a });
      const A = coh[c]!.reduce((s, h) => s + h.count, 0);
      const gM = Mo[c]! * pm, gA = (x * a + renewed) * pa;
      grossBy[c] = gM + gA;
      grossM += gM;
      grossA += gA;
      annualActive += A;
      paying += Mo[c]! + A;
    }
    const gross = grossM + grossA;
    const storeFee = gross * fee;
    const infShare = grossBy.inf! * (1 - fee) * clamp(num(d.infShare) / 100, 0, 1);
    const partShare = grossBy.part! * (1 - fee) * clamp(num(d.partnerShare) / 100, 0, 1);
    const netRev = gross - storeFee - infShare - partShare;
    const newRest = organic + adsUsers + infUsers + partUsers;
    const installs = newRest / i2p;
    const trials = Math.max(0, installs - newRest) * Math.max(0, num(d.trialMonths));
    out[k] = { n, launchConv, organic, adsBy, adsUsers, creators, infFee, infUsers, partners, partUsers, newTotal: launchConv + newRest, cancels, paying, annualActive, monthlyActive: paying - annualActive, grossM, grossA, gross, storeFee, infShare, partShare, netRev, installs, trials };
  }
  return out;
}

export function simulate(m: Model, facts: Facts, opts: { now: Date; startCash: number; ctx?: Ctx }) {
  const d = m.drivers;
  const now = opts.now;
  const ck = keyOf(now);
  const ctx = opts.ctx || { runsPaid: null, paid: new Set<string>() };
  const day = now.getDate(), dThis = dim(ck), remFrac = Math.max(0, (dThis - day) / dThis);
  const keys: string[] = [];
  for (let i = 0; i <= MAXM; i++) keys.push(addM(ck, i));
  const U = usersSim(m, keys[keys.length - 1]!);
  const policy = num(facts.policyMonths, 6);
  const debt = facts.debt || {};
  const lag = Math.round(num(d.lag, 2));
  let cash = num(opts.startCash);
  let left = Math.max(0, num(debt.balance));
  let started = false;
  let payoff: string | null = null;
  let firstPay: { key: string; amount: number } | null = null;
  const months: any[] = [];
  keys.forEach((k, i) => {
    const full = i > 0;
    const frac = full ? 1 : remFrac;
    const u = U[k] || null;
    const ex: Record<string, number> = {};
    const byGroup: Record<string, number> = {};
    GROUPS.forEach((g) => (byGroup[g.key] = 0));
    const add = (id: string, g: string, v: number) => {
      ex[id] = v;
      byGroup[g]! += v;
    };
    const raiseOn = !!d.raises && isMonth(d.raiseFrom) && k >= d.raiseFrom;
    const runs = full ? 2 : Math.max(0, 2 - (ctx.runsPaid == null ? (day < 13 ? 0 : day < 28 ? 1 : 2) : ctx.runsPaid));
    for (const o of m.owners || []) add("own:" + o.id, "people", (runs * num(raiseOn ? o.proposed : o.current)) / 24);
    for (const L of m.costs || []) {
      const g = LINE_GROUPS.includes(L.group) ? L.group : "other";
      let amt = 0;
      if (L.on !== false) {
        if (L.kind === "once") {
          if (L.month === k) amt = num(L.amount);
        } else if (!((L.start && k < L.start) || (L.end && k > L.end)))
          amt = !full && L.payee ? (ctx.paid.has(lc(L.payee)) ? 0 : num(L.amount)) : num(L.amount) * frac;
      }
      add("line:" + L.id, g, amt);
    }
    for (const E of m.events || []) add("evt:" + E.id, "events", E.month === k ? num(E.cost) : 0);
    for (const p of m.ads || []) add("ad:" + p.id, "acq", adSpend(m, p, k) * frac);
    add("inf", "acq", u ? u.infFee * frac : 0);
    const servingOn = k >= (isMonth(d.servingFrom) ? d.servingFrom : d.launch);
    const freeUsers = servingOn && (!isMonth(d.freeUntil) || k <= d.freeUntil) ? Math.max(0, num(d.freeUsers)) : 0;
    const trials = u ? u.trials : 0;
    const paying = u ? u.paying : 0;
    const served = servingOn ? paying + trials + freeUsers : 0;
    add("srv:users", "serving", served * Math.max(0, num(d.servingPerUser)) * frac);
    add("srv:base", "serving", servingOn ? Math.max(0, num(d.hostingBase)) * frac : 0);
    const opex = GROUPS.reduce((s, g) => s + byGroup[g.key]!, 0);
    const us = U[addM(k, -lag)];
    let subsCash = us ? us.netRev : 0;
    if (!full) subsCash *= frac;
    const merch = Math.max(0, num(d.merchMonthly)) * frac;
    const funding = num(d.funding) > 0 && d.fundingMonth === k ? num(d.funding) : 0;
    const opCF = subsCash + merch - opex;
    let pay = 0;
    if (d.pbMode === "written") {
      for (const p of debt.writtenPlan || []) if ((i === 0 && p.month <= k) || (i > 0 && p.month === k)) pay += num(p.amount);
      pay = Math.min(pay, left);
    } else if (full && left > 0.5) {
      const trig = d.pbStart === "month" ? isMonth(d.pbStartMonth) && k >= d.pbStartMonth : !!u && opCF > 0;
      if (!started && trig) started = true;
      if (started) {
        let want =
          d.pbMode === "fixed"
            ? Math.max(0, num(d.pbFixed))
            : d.pbMode === "revenue"
              ? subsCash * clamp(num(d.pbPctRevenue) / 100, 0, 1)
              : Math.max(0, opCF) * clamp(num(d.pbSurplusShare) / 100, 0, 1);
        if (d.pbReserve) want = Math.min(want, Math.max(0, cash + subsCash + merch + funding - opex - policy * opex));
        pay = Math.min(want, left);
      }
    }
    if (pay > 0.5 && !firstPay) firstPay = { key: k, amount: pay };
    left -= pay;
    if (left <= 0.5 && pay > 0 && !payoff) payoff = k;
    const open = cash;
    cash = open + subsCash + merch + funding - opex - pay;
    months.push({ key: k, i, full, frac, u, ex, byGroup, opex, subsCash, merch, funding, opCF, pay, left: Math.max(0, left), served, freeUsers, trials, open, end: cash, startDate: i === 0 ? now : mStart(k), periodDays: i === 0 ? Math.max(0, dThis - day) : dim(k) });
  });
  let cashOut: Date | null = null;
  for (const mo of months) {
    if (mo.open >= 0 && mo.end < 0) {
      const burn = mo.open - mo.end;
      cashOut = new Date(mo.startDate.getTime() + (burn > 0 ? (mo.open / burn) * mo.periodDays : 0) * 86400000);
      break;
    }
  }
  if (!cashOut && num(opts.startCash) < 0) cashOut = now;
  const be = months.find((mo) => mo.full && mo.u && mo.opCF >= 0);
  return { months, startCash: num(opts.startCash), cashOut, breakEven: be ? (be.key as string) : null, payoff, firstPay };
}

export function stats(sim: ReturnType<typeof simulate>, m: Model) {
  const d = m.drivers;
  const H = num(d.horizon, 18);
  const shown = sim.months.slice(0, H + 1);
  const last = shown[shown.length - 1];
  const lowest = Math.min(sim.startCash, ...shown.map((x) => x.end));
  const acqSpend = shown.reduce((s, x) => s + x.byGroup.acq, 0);
  const acqUsers = shown.reduce((s, x) => s + (x.u ? x.u.adsUsers + x.u.infUsers : 0), 0);
  return {
    H,
    lowest,
    need: Math.max(0, num(d.floor) - lowest),
    payingEnd: last.u ? last.u.paying : 0,
    netRevEnd: last.u ? last.u.netRev : 0,
    acqSpend,
    acqUsers,
    cac: acqUsers > 0 ? acqSpend / acqUsers : null,
    bdPaid: shown.reduce((s, x) => s + x.pay, 0),
    bdLeft: last.left,
  };
}

// Readable names for each expense line id, for the Finance tables.
export function lineLabels(m: Model): Record<string, { label: string; group: string }> {
  const out: Record<string, { label: string; group: string }> = {};
  for (const o of m.owners || []) out["own:" + o.id] = { label: `${o.name} (owner pay)`, group: "people" };
  for (const L of m.costs || []) out["line:" + L.id] = { label: L.name, group: LINE_GROUPS.includes(L.group) ? L.group : "other" };
  for (const E of m.events || []) out["evt:" + E.id] = { label: E.name, group: "events" };
  for (const p of m.ads || []) out["ad:" + p.id] = { label: p.name, group: "acq" };
  out.inf = { label: "Influencer fees", group: "acq" };
  out["srv:users"] = { label: "AI & hosting per user", group: "serving" };
  out["srv:base"] = { label: "Hosting base", group: "serving" };
  return out;
}
